import React, { useState, useEffect, useCallback } from 'react';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Theme, Grid, Typography } from '@material-ui/core';
import { push } from 'connected-react-router';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import { ListParams, Pagination } from '../../types/main';
import { getAllQueryData, stringifyQuery, parseQuery } from '../../utility/helpers';
import PageSizeAction from './actions/PageSizeAction';
import NextPageAction from './actions/NextPageAction';
import PreviousPageAction from './actions/PreviousPageAction';

const useStyles = makeStyles(({ palette }: Theme) => createStyles({
  root: {
    border: `solid 1px ${palette.table.border}`,
    background: '#FFF',
    fontSize: '0.875rem',
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontWeight: 400,
    color: '#263245',
  },
}), { name: 'PaginationBar' });

type Props = {
  pagination: Pagination;
  pageItems?: number[];
  countItems: number;
  fetch: (listParams: ListParams) => void;
  loading: boolean;
  onChangePageSize?: (pageSize: number) => void;
};

const getPageOf = (page: number, pageSize: number, totalItems: number): string => {
  if (totalItems === 0) {
    return '0 items';
  }
  let title = page === 0 ? '1' : Math.min(page * pageSize + 1, totalItems).toString();
  title += '-';
  title += Math.min(totalItems, page * pageSize + pageSize).toString();
  title += ' of ';
  title += totalItems.toString();
  return title;
};

const PaginationBar = ({
  pagination, fetch, pageItems, countItems, loading, onChangePageSize,
}: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const [page, setPage] = useState(pagination.page);
  const [totalItems, setTotalItems] = useState(pagination.totalItems);
  const [pageSize, setPageSize] = useState(pagination.pageSize);

  const pages = Math.ceil(totalItems / pageSize);
  const pageOf = getPageOf(page, pageSize, totalItems);

  useEffect(() => {
    const queryParams = parseQuery(search);

    if (countItems === 0 && pagination.totalItems > 0 && !loading && search) {
      const params = {
        ...queryParams,
        page,
      } as ListParams;
      dispatch(push({ search: stringifyQuery(params) }));
      fetch(params);
    }

    if ((pagination.page !== page || pagination.pageSize !== pageSize) && !loading) {
      const params = {
        ...queryParams,
        page: pagination.page,
        page_size: pagination.pageSize,
      } as ListParams;
      setPage(pagination.page);
      setPageSize(pagination.pageSize);
      dispatch(push({ search: stringifyQuery(params) }));
    }

    if (pagination.totalItems !== totalItems && !loading) {
      setTotalItems(pagination.totalItems);
    }
  }, [
    dispatch,
    totalItems,
    setTotalItems,
    pagination.totalItems,
    pagination.page,
    pagination.pageSize,
    countItems,
    page,
    setPage,
    pageSize,
    setPageSize,
    search,
    fetch,
    loading,
  ]);

  const changePage = useCallback((pageValue: number) => {
    const queryParams = getAllQueryData();
    const params = {
      ...queryParams,
      page: pageValue,
    } as ListParams;
    setPage(pageValue);
    dispatch(push({ search: stringifyQuery(params) }));
    fetch(params);
  }, [fetch, setPage, dispatch]);

  const changePageSize = useCallback((event: React.ChangeEvent<{value: unknown}>) => {
    const value = Number(event.target.value);
    if (onChangePageSize) {
      onChangePageSize(value);
    }
    setPageSize(value);
    const queryParams = getAllQueryData();
    const params = {
      ...queryParams,
      page_size: value,
      page: 0,
    } as ListParams;

    dispatch(push({ search: stringifyQuery(params) }));
    fetch(params);
  }, [setPageSize, dispatch, fetch, onChangePageSize]);

  return (
    <Grid
      container
      className={classes.root}
      justify="flex-end"
      alignItems="center"
      alignContent="center"
    >
      <PageSizeAction changePageSize={changePageSize} pageSize={pageSize} pageItems={pageItems} />
      <Grid item>
        <Typography>
          {pageOf}
        </Typography>
      </Grid>
      <Grid item>
        <PreviousPageAction changePage={changePage} page={page} />
        <NextPageAction changePage={changePage} page={page} pages={pages} />
      </Grid>
    </Grid>
  );
};

export default PaginationBar;
